/* eslint-disable-next-line no-unused-vars */
const Types = require('../../../../../jsdoc-types');

const { default: getFrontConfig } = require('../../common/getFrontConfig');

const {
  urls: { giftCardCheckBalanceForBasket },
} = getFrontConfig();

const GIFTCARD_NUMBER_ID = 'giftcardNumber';
const GIFTCARD_CVV_ID = 'giftcardCvv';
const GIFTCARD_RESPONSE_DATA_ID = 'giftcardResponseData';

/**
 * @typedef GiftCardData
 * @property {string} cardNumber - The gift card number
 * @property {string} cvv - The gift card CVV
 */

/**
 * @typedef GiftCardTotals
 * @property {number} balance - The numeric balance of the gift card
 * @property {string} formattedBalance - The formatted balance of the gift card
 * @property {string} formattedRemainingBalance - The formatted remaining balance of the gift card
 * @property {string} spentGiftCardTotal - The formatted spent total value from the gift card
 */

/**
 * @typedef DisplayTotals
 * @property {number} floatTotal - The numeric grand total after applying the gift card
 * @property {string} grandTotal - The formatted grand total after applying the gift card
 */

/**
 * @typedef {Types.Totals & { spentGiftCardTotal: string }} TotalsWithGiftCardTotals
 */

/**
 * @typedef GiftCardBalanceResponse
 * @property {boolean} coversOrderTotal - Indicates if the gift card balance covers the order total
 * @property {GiftCardTotals} [giftCardTotals] - Gift card balance and spent totals
 * @property {DisplayTotals} [displayTotals] - Adjusted order totals after applying the gift card
 * @property {boolean} [error] - Indicates if there was an error checking the gift card balance
 * @property {string} [errorMessage] - The error message
 */

/**
 * Gets gift card data from the DOM
 * @returns {GiftCardData} The gift card data object containing the card number and CVV
 */
function getGiftCardDataFromDOM() {
  return {
    cardNumber: document.getElementById(GIFTCARD_NUMBER_ID)?.value || '',
    cvv: document.getElementById(GIFTCARD_CVV_ID)?.value || '',
  };
}

/**
 * Saves the gift card balance response data to a hidden DOM input as JSON
 * @param {GiftCardBalanceResponse} giftCardBalanceResponse - The gift card balance response
 */
function saveGiftCardResponseData(giftCardBalanceResponse) {
  const el = document.getElementById(GIFTCARD_RESPONSE_DATA_ID);
  if (!el) return;
  el.value = JSON.stringify({
    balance: giftCardBalanceResponse.giftCardTotals?.balance ?? 0,
    formattedRemainingBalance:
      giftCardBalanceResponse.giftCardTotals?.formattedRemainingBalance ?? '',
    spentGiftCardTotal:
      giftCardBalanceResponse.giftCardTotals?.spentGiftCardTotal ?? '',
    floatTotal: giftCardBalanceResponse.displayTotals?.floatTotal ?? '',
    grandTotal: giftCardBalanceResponse.displayTotals?.grandTotal ?? '',
  });
}

/**
 * Reads the saved gift card response data from the hidden DOM input
 * @returns {{ balance: number, formattedRemainingBalance: string, grandTotal: string,
 *   floatTotal: number, spentGiftCardTotal: string }} The parsed response data
 */
function getGiftCardResponseDataFromDOM() {
  try {
    return JSON.parse(
      document.getElementById(GIFTCARD_RESPONSE_DATA_ID)?.value || '{}'
    );
  } catch {
    return {};
  }
}

/**
 * Checks the balance of a gift card.
 * Returns a promise that resolves to a GiftCardBalanceResponse object.
 * @returns {Promise<GiftCardBalanceResponse>} A promise that resolves to the gift card balance response
 */
async function checkGiftCardBalance() {
  /** @type {GiftCardBalanceResponse} */
  const giftCardBalanceResponse = {
    coversOrderTotal: false,
  };

  const giftCardData = getGiftCardDataFromDOM();

  await fetch(giftCardCheckBalanceForBasket, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(giftCardData),
  })
    .then((response) => response.json())
    .then((data) => {
      if (data.error) {
        giftCardBalanceResponse.error = true;
        giftCardBalanceResponse.errorMessage = data.errorMessage || '';
      } else {
        giftCardBalanceResponse.coversOrderTotal = data.coversOrderTotal;
        giftCardBalanceResponse.giftCardTotals = data.giftCardTotals;
        giftCardBalanceResponse.displayTotals = data.displayTotals;

        saveGiftCardResponseData(giftCardBalanceResponse);

        // Update Checkout UI from response
        $('body').trigger('checkout:updateCheckoutView', {
          order: data.order,
          customer: data.customer,
        });
      }
    });

  return giftCardBalanceResponse;
}

/**
 * Adjusts the totals object to reflect the gift card discount.
 * Reads the pre-computed adjusted totals from the DOM (set by the last balance check).
 * @param {Types.Totals} totals - The current order totals
 * @returns {TotalsWithGiftCardTotals} A new totals object with grandTotal and floatTotal adjusted
 */
function adjustTotalsForGiftCard(totals) {
  const { floatTotal, grandTotal, spentGiftCardTotal } =
    getGiftCardResponseDataFromDOM();
  return {
    ...totals,
    floatTotal: parseFloat(floatTotal) || 0,
    grandTotal: grandTotal ?? '',
    spentGiftCardTotal: spentGiftCardTotal ?? '',
  };
}

module.exports = {
  methods: {
    adjustTotalsForGiftCard,
    checkGiftCardBalance,
    getGiftCardResponseDataFromDOM,
  },
};
